import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import TrendChart from './TrendChart';
import PeakHoursHeatmap from './PeakHoursHeatmap';
import CallHistoryModal from './CallHistoryModal';
import {
  getCallStatus,
  hadTalkTime,
  filterCallsByDate,
  localDateStr,
  daysAgo,
  buildDailyPoints,
  buildHeatmapGrid,
  formatAvgDuration,
  formatFollowUpDate,
  groupByCustomer,
  getKnownAgents,
} from './callUtils';

const PRESETS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7', label: '7 Days', days: 7 },
  { key: '14', label: '14 Days', days: 14 },
  { key: '30', label: '30 Days', days: 30 },
];

// A call belongs to this agent once they're the one who answered it — the
// same attribution buildAgentStats uses, just pre-filtered to one identity
// instead of grouped across all of them.
const belongsToAgent = (call, agentNumber) => call.answered_agent_number === agentNumber;

function MyDashboard({ calls }) {
  const { user, authFetch } = useAuth();
  const [followUps, setFollowUps] = useState([]);
  const [historyNumber, setHistoryNumber] = useState(null);

  const today = localDateStr(new Date());
  const [fromDate, setFromDate] = useState(daysAgo(7));
  const [toDate, setToDate] = useState(today);

  const activePreset = PRESETS.find((preset) => fromDate === daysAgo(preset.days) && toDate === today)?.key;

  const applyPreset = (preset) => {
    setFromDate(daysAgo(preset.days));
    setToDate(today);
  };

  useEffect(() => {
    let cancelled = false;
    authFetch('/api/follow-ups')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setFollowUps(Array.isArray(data) ? data : []);
      })
      .catch((error) => console.error('Error fetching follow-ups:', error));
    return () => {
      cancelled = true;
    };
  }, [authFetch]);

  const myCalls = useMemo(
    () => (user?.agentNumber ? calls.filter((call) => belongsToAgent(call, user.agentNumber)) : []),
    [calls, user]
  );

  const rangeCalls = useMemo(() => filterCallsByDate(myCalls, fromDate, toDate), [myCalls, fromDate, toDate]);
  const points = useMemo(() => buildDailyPoints(myCalls, fromDate, toDate), [myCalls, fromDate, toDate]);
  const heatmapGrid = useMemo(() => buildHeatmapGrid(rangeCalls), [rangeCalls]);

  const totals = useMemo(() => {
    const acc = { answered: 0, missed: 0, attempted: 0, answeredBillsec: 0 };
    for (const call of rangeCalls) {
      const status = getCallStatus(call);
      if (status === 'Answered') {
        acc.answered += 1;
        if (hadTalkTime(call)) acc.answeredBillsec += Number(call.billsec);
      } else if (status === 'Missed') acc.missed += 1;
      else if (status === 'Attempted') acc.attempted += 1;
    }
    return acc;
  }, [rangeCalls]);

  const totalCalls = totals.answered + totals.missed + totals.attempted;
  const answerRate = totalCalls > 0 ? Math.round((totals.answered / totalCalls) * 100) : null;

  // Whoever created a follow-up is stored as the agent's display name at the
  // time (agent_name if set, else username) — same value agentDisplayNameForRequest
  // produces server-side (see server/index.js). That's the only link back to
  // "my" follow-ups, since a follow-up is tied to a customer, not a call leg.
  const myIdentity = user?.agentName || user?.username;
  const myFollowUps = useMemo(
    () => followUps.filter((followUp) => followUp.created_by === myIdentity),
    [followUps, myIdentity]
  );
  const pendingFollowUps = myFollowUps
    .filter((followUp) => followUp.follow_up_status !== 'done')
    .sort((a, b) => new Date(a.follow_up_date || 0) - new Date(b.follow_up_date || 0));

  const now = Date.now();
  const isOverdue = (followUp) => followUp.follow_up_date && new Date(followUp.follow_up_date).getTime() < now;

  const groupedCalls = useMemo(() => groupByCustomer(calls), [calls]);
  const knownAgents = useMemo(() => getKnownAgents(calls), [calls]);

  if (!user?.agentNumber) {
    return (
      <div className="agent-page">
        <p className="agent-page-empty">
          Your account isn't linked to an agent identity yet. Ask a superadmin to set this in Manage Admins so your
          calls can be tracked here.
        </p>
      </div>
    );
  }

  return (
    <div className="agent-page">
      <div className="agent-page-header">
        <h2 className="agent-page-title">Welcome, {user.agentName || user.username}</h2>
        <div className="agent-date-filter">
          <div className="customer-toggle" role="group" aria-label="Date range preset">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={activePreset === preset.key ? 'active' : ''}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="stat-cards">
        <div className="stat-card stat-card-static">
          <div className="stat-value">{totalCalls}</div>
          <div className="stat-label">Total Calls</div>
        </div>
        <div className="stat-card stat-card-static answered">
          <div className="stat-value">{totals.answered}</div>
          <div className="stat-label">Answered</div>
        </div>
        <div className="stat-card stat-card-static missed">
          <div className="stat-value">{totals.missed}</div>
          <div className="stat-label">Missed</div>
        </div>
        <div className="stat-card stat-card-static attempted">
          <div className="stat-value">{totals.attempted}</div>
          <div className="stat-label">Attempted</div>
        </div>
        <div className="stat-card stat-card-static">
          <div className="stat-value">{answerRate === null ? '-' : `${answerRate}%`}</div>
          <div className="stat-label">Answer Rate</div>
        </div>
        <div className="stat-card stat-card-static">
          <div className="stat-value">{formatAvgDuration(totals.answeredBillsec, totals.answered)}</div>
          <div className="stat-label">Avg Handle Time</div>
        </div>
      </div>

      <TrendChart points={points} />
      <PeakHoursHeatmap grid={heatmapGrid} />

      <div className="chart-card">
        <div className="trend-chart-header">
          <h3 className="heatmap-title">My Pending Follow-Ups</h3>
        </div>
        {pendingFollowUps.length === 0 ? (
          <p className="agent-page-empty">No pending follow-ups.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Number</th>
                  <th>Priority</th>
                  <th>Follow-up Date/Time</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {pendingFollowUps.map((followUp) => {
                  const overdue = isOverdue(followUp);
                  const rowClass = overdue ? 'row-critical' : followUp.follow_up_priority === 'high' ? 'row-priority-high' : '';
                  return (
                    <tr key={followUp.id} className={rowClass}>
                      <td className="table-cell-strong">{followUp.customer_name || '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="customer-number-link"
                          onClick={() => setHistoryNumber(followUp.customer_number)}
                          title="View call history for this number"
                        >
                          {followUp.customer_number}
                        </button>
                      </td>
                      <td>
                        {followUp.follow_up_priority ? (
                          <span className={`status follow-up-priority-${followUp.follow_up_priority}`}>
                            {followUp.follow_up_priority}
                          </span>
                        ) : (
                          '-'
                        )}
                        {overdue && <span className="status critical table-critical-badge">Overdue</span>}
                      </td>
                      <td>{followUp.follow_up_date ? formatFollowUpDate(followUp.follow_up_date) : '-'}</td>
                      <td className="table-cell-truncate" title={followUp.follow_up_note || ''}>
                        {followUp.follow_up_note || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {historyNumber && (
        <CallHistoryModal
          group={groupedCalls.find((group) => group.customerNumber === historyNumber)}
          onClose={() => setHistoryNumber(null)}
          agents={knownAgents}
          onCallTriggered={() => {}}
          initialShowFollowUp
        />
      )}
    </div>
  );
}

export default MyDashboard;
